import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, Clock3, MapPin, Trash2, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { requestErrorMessage } from "@/lib/requestError";
import { useUnsavedChanges } from "@/navigation/unsavedChanges";

const editableFields = ["title", "starts_at", "ends_at", "all_day", "event_type", "location", "notes", "project_id", "assigned_user_ids"];
const draftIdentity = form => JSON.stringify(editableFields.map(field => form[field]));

const blankAppointment = (date = new Date()) => {
  const start = new Date(date);
  start.setHours(9, 0, 0, 0);
  const end = new Date(start);
  end.setHours(10, 0, 0, 0);
  return {
    title: "",
    starts_at: start.toISOString(),
    ends_at: end.toISOString(),
    all_day: false,
    event_type: "appointment",
    location: "",
    notes: "",
    project_id: "",
    assigned_user_ids: [],
  };
};

const dateTimeInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const dateInputValue = (value) => dateTimeInputValue(value).slice(0, 10);

const asIso = (value, allDay, isEnd) => {
  if (!value) return "";
  const normalized = allDay
    ? `${value}T${isEnd ? "23:59" : "00:00"}`
    : value;
  return new Date(normalized).toISOString();
};

export const AppointmentEditorDialog = ({
  appointment,
  defaultDate,
  onDelete,
  onOpenChange,
  onSave,
  open,
  lockedProjectId = "",
  lockedProjectLabel = "",
  projects,
  users,
}) => {
  const [form, setForm] = useState(blankAppointment(defaultDate));
  const baseline = useRef(form);
  const sourceKey = useRef("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const savingRef = useRef(false);
  const isNew = !appointment?.id;
  const canEdit = isNew || appointment?.can_edit;

  useEffect(() => {
    const key = `${open}:${appointment?.id || "new"}:${lockedProjectId}`;
    if (sourceKey.current === key) return;
    sourceKey.current = key;
    const next = {
      ...(appointment || blankAppointment(defaultDate)),
      project_id: lockedProjectId || appointment?.project_id || "",
    };
    baseline.current = next;
    setForm(next);
  }, [appointment, defaultDate, lockedProjectId, open]);

  const assigneeNames = useMemo(
    () =>
      users
        .filter((user) => form.assigned_user_ids.includes(user.id))
        .map((user) => user.name),
    [form.assigned_user_ids, users],
  );

  const setValue = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  const toggleAssignee = (userId) => {
    setForm((current) => ({
      ...current,
      assigned_user_ids: current.assigned_user_ids.includes(userId)
        ? current.assigned_user_ids.filter((id) => id !== userId)
        : [...current.assigned_user_ids, userId],
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!canEdit || savingRef.current) return false;
    setError("");
    if (!form.title.trim() || form.title.trim().length < 2) {
      setError("Enter an appointment title with at least two characters.");
      return false;
    }
    if (!form.starts_at || !form.ends_at || !Number.isFinite(Date.parse(form.starts_at)) ||
        !Number.isFinite(Date.parse(form.ends_at)) || Date.parse(form.ends_at) <= Date.parse(form.starts_at)) {
      setError("Appointment end time must be after its start time.");
      return false;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      await onSave({
        title: form.title.trim(),
        all_day: form.all_day,
        event_type: form.event_type,
        location: form.location || "",
        notes: form.notes || "",
        assigned_user_ids: [...form.assigned_user_ids],
        project_id: lockedProjectId || form.project_id,
        starts_at: asIso(
          form.all_day ? dateInputValue(form.starts_at) : dateTimeInputValue(form.starts_at),
          form.all_day,
          false,
        ),
        ends_at: asIso(
          form.all_day ? dateInputValue(form.ends_at) : dateTimeInputValue(form.ends_at),
          form.all_day,
          true,
        ),
      });
      return true;
    } catch (failure) {
      setError(requestErrorMessage(failure, "Appointment could not be saved. Your changes are still here."));
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!appointment?.id || !canEdit || savingRef.current) return;
    if (!window.confirm("Delete this appointment?")) return;
    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      await onDelete(appointment.id);
    } catch (failure) {
      setError(requestErrorMessage(failure, "Appointment could not be deleted. Please try again."));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const guard = useUnsavedChanges({ dirty: open && canEdit && draftIdentity(form) !== draftIdentity(baseline.current),
    label: "Appointment", busy: saving, onSave: () => submit({ preventDefault() {} }),
    onDiscard: () => setForm(baseline.current) });

  return (
    <Dialog onOpenChange={next => next ? onOpenChange(true) : guard.requestClose(() => onOpenChange(false))} open={open}>
      <DialogContent
        className="appointment-dialog"
        data-testid="appointment-editor-dialog"
      >
        <DialogHeader>
          <p className="operations-kicker">
            {isNew ? "New appointment" : "Appointment details"}
          </p>
          <DialogTitle data-testid="appointment-editor-title">
            {isNew ? "Schedule an appointment" : appointment.title}
          </DialogTitle>
          <DialogDescription>
            {canEdit
              ? "Appointments coordinate people and awareness only. They never change project scheduling or status."
              : "You can view this appointment, but only its creator, Company Owner/Admin, or Office/Admin can change it."}
          </DialogDescription>
        </DialogHeader>
        <form data-testid="appointment-editor-form" onSubmit={submit} noValidate>
          {error && <p role="alert">{error}</p>}
          <div className="appointment-editor-grid">
            <label className="appointment-editor-wide" htmlFor="appointment-title-input">
              Appointment title
              <Input
                data-testid="appointment-title-input"
                disabled={!canEdit}
                id="appointment-title-input"
                onChange={(event) => setValue("title", event.target.value)}
                required
                value={form.title}
              />
            </label>
            <label htmlFor="appointment-type-select">
              Type
              <select
                data-testid="appointment-type-select"
                disabled={!canEdit}
                id="appointment-type-select"
                onChange={(event) => setValue("event_type", event.target.value)}
                value={form.event_type}
              >
                <option value="appointment">Appointment</option>
                <option value="meeting">Meeting</option>
                <option value="inspection">Inspection</option>
                <option value="reminder">Reminder</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="appointment-all-day-toggle">
              <input
                checked={form.all_day}
                data-testid="appointment-all-day-toggle"
                disabled={!canEdit}
                onChange={(event) => setValue("all_day", event.target.checked)}
                type="checkbox"
              />
              All day
            </label>
            <label htmlFor="appointment-start-input">
              <Clock3 size={14} /> Starts
              <Input
                data-testid="appointment-start-input"
                disabled={!canEdit}
                id="appointment-start-input"
                onChange={(event) =>
                  setValue(
                    "starts_at",
                    asIso(event.target.value, form.all_day, false),
                  )
                }
                required
                type={form.all_day ? "date" : "datetime-local"}
                value={
                  form.all_day
                    ? dateInputValue(form.starts_at)
                    : dateTimeInputValue(form.starts_at)
                }
              />
            </label>
            <label htmlFor="appointment-end-input">
              <Clock3 size={14} /> Ends
              <Input
                data-testid="appointment-end-input"
                disabled={!canEdit}
                id="appointment-end-input"
                onChange={(event) =>
                  setValue(
                    "ends_at",
                    asIso(event.target.value, form.all_day, true),
                  )
                }
                required
                type={form.all_day ? "date" : "datetime-local"}
                value={
                  form.all_day
                    ? dateInputValue(form.ends_at)
                    : dateTimeInputValue(form.ends_at)
                }
              />
            </label>
            <label htmlFor="appointment-location-input">
              <MapPin size={14} /> Location
              <Input
                data-testid="appointment-location-input"
                disabled={!canEdit}
                id="appointment-location-input"
                onChange={(event) => setValue("location", event.target.value)}
                value={form.location}
              />
            </label>
            {lockedProjectId ? (
              <div data-testid="project-appointment-fixed-project">
                <span>Project</span>
                <strong>{lockedProjectLabel}</strong>
              </div>
            ) : <label htmlFor="appointment-project-select">
              <CalendarDays size={14} /> Optional project
              <select
                data-testid="appointment-project-select"
                disabled={!canEdit}
                id="appointment-project-select"
                onChange={(event) => setValue("project_id", event.target.value)}
                value={form.project_id}
              >
                <option value="">No project link</option>
                {projects.map((project) => (
                  <option
                    key={project.id}
                    label={`${project.project_code} · ${project.name}`}
                    value={project.id}
                  />
                ))}
              </select>
            </label>}
            <label className="appointment-editor-wide" htmlFor="appointment-notes-input">
              Notes
              <Textarea
                data-testid="appointment-notes-input"
                disabled={!canEdit}
                id="appointment-notes-input"
                onChange={(event) => setValue("notes", event.target.value)}
                value={form.notes}
              />
            </label>
          </div>
          <section className="appointment-participants" data-testid="appointment-participants">
            <div>
              <UsersRound size={15} />
              <strong>Participants</strong>
              <span>
                {assigneeNames.length
                  ? assigneeNames.join(", ")
                  : "No participants assigned"}
              </span>
            </div>
            <div className="appointment-participant-options">
              {users.map((user) => (
                <label key={user.id}>
                  <input
                    checked={form.assigned_user_ids.includes(user.id)}
                    data-testid={`appointment-assignee-${user.id}`}
                    disabled={!canEdit}
                    onChange={() => toggleAssignee(user.id)}
                    type="checkbox"
                  />
                  {user.name}
                </label>
              ))}
            </div>
          </section>
          {!isNew && (
            <section className="appointment-readonly-meta" data-testid="appointment-creator-details">
              <span>Created by</span>
              <strong>{appointment.created_by_name_snapshot || "Unknown"}</strong>
              <small>{appointment.created_by_role_snapshot?.replaceAll("_", " ")}</small>
            </section>
          )}
          {canEdit && (
            <DialogFooter>
              {!isNew && (
                <Button
                  data-testid="appointment-delete-button"
                  disabled={saving}
                  onClick={remove}
                  type="button"
                  variant="outline"
                >
                  <Trash2 size={15} />
                  Delete
                </Button>
              )}
              <Button
                data-testid="appointment-save-button"
                disabled={saving}
                type="submit"
              >
                <CalendarDays size={15} />
                {saving ? "Saving…" : isNew ? "Create appointment" : "Save changes"}
              </Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
